import { useCallback, useEffect, useMemo } from 'react';
import tw from 'twin.macro';
import { useAtom } from 'jotai';
import { useTranslation } from 'next-i18next';

import { EmployerRestaurantCard } from '@restworld/ui-ds';
import { useAnalytics, useKv } from '@restworld/utils-common';
import { useAuth } from '@restworld/feature-authentication';

import { RestaurantInfo } from '../hooks/use-employer-restaurants';
import { BorderTrail } from '../../motion-primitives/border-trail';
import { newlyCreatedRestaurantIdAtom } from '../restaurant-onboarding-modal/restaurant-onboarding-successful';

function useEmployerRestaurantCards({ restaurants }: { restaurants?: RestaurantInfo[] }) {
  const [newlyCreatedRestaurantId, setNewlyCreatedRestaurantId] = useAtom(newlyCreatedRestaurantIdAtom);
  const { t: tC } = useTranslation('common');
  const { getKV, setKV } = useKv();
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();

  const kvKey = useMemo(() => (user?.id ? `${user.id}-newly-created-venue-id` : ''), [user?.id]);

  const handleCardClick = useCallback(
    ({
      isNewlyCreated,
      resturantName,
      openJps,
    }: {
      isNewlyCreated: boolean;
      resturantName: string;
      openJps: number;
    }) => {
      if (isNewlyCreated && kvKey) {
        setKV({ key: kvKey, value: undefined, type: 'employer_newly_created_venue_id' });
        setNewlyCreatedRestaurantId('');
      }
      trackEvent('employer_view_locali_details', {
        restaurant_name: resturantName,
        open_jps: openJps,
        email: user?.email,
      });
    },
    [kvKey, setKV, setNewlyCreatedRestaurantId, trackEvent, user?.email]
  );

  useEffect(() => {
    if (!kvKey) return;

    getKV(kvKey)
      .then((data) => {
        setNewlyCreatedRestaurantId(data?.value);
      })
      .catch((e) => console.error(e));
  }, [getKV, kvKey, setNewlyCreatedRestaurantId]);

  const restaurantCards = useMemo(
    () =>
      restaurants?.map(
        ({
          notViewedNormalJobApplicantsAvatar,
          notViewedSpontaneousApplicantsAvatar,
          notViewedNormalJobApplicationCount,
          notViewedSpontaneousApplicationCount,
          restaurant: {
            id,
            address,
            active_job_positions,
            pictures,
            restaurant_logo,
            restaurant_name,
            types,
            slug,
            show_details,
          },
        }) => {
          const candidatesCount =
            (notViewedNormalJobApplicationCount || 0) + (notViewedSpontaneousApplicationCount || 0);

          const applicants = [
            ...(notViewedNormalJobApplicantsAvatar || []),
            ...(notViewedSpontaneousApplicantsAvatar || []),
          ];

          const employerCandidates = applicants.map((candidate) => ({
            firstName: candidate.name,
            lastName: candidate.surname,
            image: candidate.avatar_url,
          }));

          const isAllSpontaneous = !!notViewedSpontaneousApplicationCount && !notViewedNormalJobApplicationCount;

          const isNewlyCreated = id === newlyCreatedRestaurantId;

          let candidatesDisplayMessage: string;
          if (isAllSpontaneous) candidatesDisplayMessage = tC('spontaneous_applications');
          else if (candidatesCount === 1) candidatesDisplayMessage = tC('new_candidate_to_view');
          else candidatesDisplayMessage = tC('candidates_to_view');

          const restaurantCardProps = {
            restaurantName: restaurant_name,
            restaurantLogo: restaurant_logo,
            address,
            openJpsCount: active_job_positions?.length,
            pictures,
            types,
            href: `/employer/venues/${slug}`,
            candidates: {
              count: candidatesCount,
              candidates: employerCandidates,
              message: candidatesDisplayMessage,
              isAllSpontaneous,
            },
            onClick: () =>
              handleCardClick({
                isNewlyCreated,
                resturantName: restaurant_name,
                openJps: active_job_positions?.length,
              }),
            isShowcaseOffline: !show_details,
            isHighlighted: isNewlyCreated,
          };
          return (
            <div key={id}>
              <div tw="relative rounded-2xl">
                <EmployerRestaurantCard key={id} {...restaurantCardProps} />
                {isNewlyCreated && (
                  <BorderTrail
                    twStyles={[
                      tw`bg-[linear-gradient(to left, var(--tw-gradient-stops))] from-purple-200 via-purple-400 to-purple-700 transition-opacity duration-300 dark:from-green-700/30 dark:via-green-500 dark:to-green-700/30`,
                    ]}
                    size={320}
                    wrapperStyles={[tw`border-[1px]`]}
                  />
                )}
              </div>
            </div>
          );
        }
      ),
    [restaurants, newlyCreatedRestaurantId, tC, handleCardClick]
  );

  return restaurantCards;
}

export default useEmployerRestaurantCards;
